import React, { useEffect, useState, useCallback } from 'react';
import styles from './CreateEvaluationOption.module.scss';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import Button from '../../../components/button/Button';
import TextField from '../../../components/textField/TextField';
import TextAreaField from '../../../components/textAreaField/TextAreaField';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';

const CreateEvaluationOption = () => {
  const { optionId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    point: '',
    minPercentage: '',
    maxPercentage: '',
    description: '',
  });

  const fetchOption = useCallback(async () => {
    if (!optionId) return;
    setIsLoading(true);
    try {
      const response = await axiosClient.get(`/options/${optionId}`);
      const data = response.data?.option || response.data || response;
      setFormData({
        name: data.name || '',
        point: data.point ?? '',
        minPercentage: data.minPercentage ?? '',
        maxPercentage: data.maxPercentage ?? '',
        description: data.description || '',
      });
    } catch (error) {
      showToast(error.message, 'error');
      navigate('/evaluation-options');
    } finally {
      setIsLoading(false);
    }
  }, [optionId, showToast, navigate]);

  useEffect(() => {
    fetchOption();
  }, [fetchOption]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (formData.point === '' || formData.point === null) newErrors.point = 'Point is required';
    if (formData.minPercentage === '') newErrors.minPercentage = 'Min % is required';
    if (formData.maxPercentage === '') newErrors.maxPercentage = 'Max % is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        name: formData.name,
        point: parseFloat(formData.point),
        minPercentage: parseFloat(formData.minPercentage),
        maxPercentage: parseFloat(formData.maxPercentage),
        description: formData.description,
      };
      if (optionId) {
        await axiosClient.patch(`/options/${optionId}`, payload);
        showToast('Option updated successfully');
      } else {
        await axiosClient.post('/options/create', payload);
        showToast('Option created successfully');
      }
      navigate('/evaluation-options');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Evaluation Options', path: '/evaluation-options' },
            { label: optionId ? 'Edit Option' : 'Create Option' },
          ]}
        />
      </div>
      <div className={styles.container}>
        {(isLoading || isSaving) && <LoadingOverlay />}
        <div className={styles.form}>
          <div className={styles.row}>
            <div className={styles.column}>
              <TextField
                label="Name *"
                value={formData.name}
                onChange={(val) => handleChange('name', val)}
                placeholder="Enter option name"
                error={errors.name}
              />
            </div>
            <div className={styles.column}>
              <TextField
                label="Point *"
                type="number"
                value={formData.point}
                onChange={(val) => handleChange('point', val)}
                placeholder="Enter point value"
                error={errors.point}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column}>
              <TextField
                label="Min Percentage *"
                type="number"
                value={formData.minPercentage}
                onChange={(val) => handleChange('minPercentage', val)}
                placeholder="Enter min percentage"
                error={errors.minPercentage}
              />
            </div>
            <div className={styles.column}>
              <TextField
                label="Max Percentage *"
                type="number"
                value={formData.maxPercentage}
                onChange={(val) => handleChange('maxPercentage', val)}
                placeholder="Enter max percentage"
                error={errors.maxPercentage}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column}>
              <TextAreaField
                label="Description"
                value={formData.description}
                onChange={(val) => handleChange('description', val)}
                placeholder="Enter description"
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={() => navigate('/evaluation-options')}
            />
            <Button
              label={optionId ? 'Save' : 'Create Option'}
              variant="primary"
              size="medium"
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEvaluationOption;
